import { IServiceInfo } from "@/domain/entities/serviceInfo/serviceInfo.types";
import { Result } from "neverthrow";
import { HttpError } from "../shared/errors/httpError";
import { ParseError } from "../shared/errors/parseError";
import { HttpRankCode } from "../user/userRepository.types";

export type HttpPasswordStrength = {
  lengthLowerBound: number;
  lengthUpperBound: number;
  mustIncludeAlphaNumeric: boolean;
  mustIncludeSymbol: boolean;
  mustIncludeUpperLower: boolean;
};

export type HttpSignupField = {
  key?: string; // JOIN.CLIENT_COMPANY
  type?: string; // option
  label?: string; // 소속회사
  caption?: string; // 소속회사를 선택하세요.
  datasource?: string; // /company?useRankCode=MUL-REG
  inputField?: string; // clientUid
  requiredYn?: boolean;
  valueField?: string; // uid
  displayTextField?: string; // name
  directInputYn?: boolean;
  parentKey?: string; // JOIN.CLIENT_COMPANY_BP
  datasourceField?: string;
  datasourceApi?: string;
  certRequiredYn?: boolean;
  certCaption?: string; // 이메일 입증번호를 입력하세요.
  certInputField?: string; // emailCertNo
  certLabel?: string; // 이메일 인증번호
  authRequiredForEditYn?: boolean; // 수정시, 본인인증 필요 여부
  updatableYn?: boolean; // 수정 가능여부
  passwordStrength?: HttpPasswordStrength;
  excludeUserInfoYn?: boolean; // 회원정보/수정 화면에 불포함 여부
  autoFilledJoinYn?: boolean; // 회원가입시 자동 입력되는 필드인지 여부
};

export type HttpSignupInfo = {
  note?: string;
  joinText?: string;
  joinFirst?: boolean;
  joinFields?: HttpSignupField[];
  useRankCode?: HttpRankCode;
};

export enum HttpTermsTypeCode {
  USAGE = "AGC-USAGE",
  PERSON = "AGC-PERSON",
  PERSON_FULL = "AGC-PERFUL",
  MARKET = "AGC-MARKET",
  PERLOC = "AGC-PERLOC",
  LOCATION = "AGC-LOCATI",
}

export type HttpTermsInfo = {
  url: string;
  code: HttpTermsTypeCode;
  title: string;
  required: boolean;
  labelPrefix?: string;
  hideCheckboxYn?: boolean;
};

export type HttpTwcConfig = {
  brandKey: string;
  scenarioId: string;
  channelType: string;
};

export type HttpColor = {
  primary?: string;
  primaryDark?: string;
  primaryAlpha3?: string;
  primaryAlpha5?: string;
  primaryAlpha10?: string;
  primaryLight?: string;
  secondPrimary?: string;
  success?: string;
  error?: string;
  subColor?: string;
};

export type HttpAppPermissions = {
  android?: HttpAppPermission[];
  ios?: HttpAppPermission[];
};

export type HttpAppPermission = {
  name: string;
  description: string;
  required: boolean;
};

export interface HttpPathColor {
  order: number;
  color: string;
  markerCdn: string;
}

export interface HttpBusStopLayout {
  campusName: string;
  url: string;
}

export interface HttpCampaignImage {
  uid: string;
  imageTypeCode: string;
  imageTypeCodeName: string;
  displayOrder: number;
  name: string;
  url: string;
}

export interface HttpCampaign {
  uid: string;
  name: string;
  displayOrder: number;
  images: HttpCampaignImage[];
  topYn: boolean;
  loginHtml?: string;
  logoutHtml?: string;
  useBeginDtm: string;
  useEndDtm?: string;
  bannerIconColor: string;
  altText?: string;
}

export type HttpServiceInfo = {
  uid: string;
  name: string;
  managerCertYn: boolean; // 가입시 담당자 승인 여부
  publicLineApiYn: boolean;
  publicTalkApiYn: boolean;
  passwordValidIntervalDay: number;
  passwordChangeAlarmIntervalDay: number; // 비밀번호 만료 전 알림 기간
  passwordNextChangeLimit: number; // 비밀번호 다음에 변경하기가 가능한 횟수
  bookTimeoutIntervalMin?: number; // 예약 진행 유효 기간 (분)
  certValidIntervalMin?: number; // 인증 유효 기간 (분)
  bookWaitYn?: boolean; // 예약 대기 여부
  boardCheckCode?: string; // 탑승 확인 방법 코드
  maxLinePathSelectCount?: number; // 경로 비교할 노선 최대 개수
  linePathColors?: HttpPathColor[]; // 지도에 표시될 노선경로의 컬러
  campaigns?: HttpCampaign[];
  talkProps?: {
    maxDailyCreationCount?: number;
    maxFileUploadCount?: number;
  };
  vocProps?: {
    maxFileUploadCount?: number;
    maxDailyCreationCount?: number;
  };
  lostFoundProps?: {
    maxDailyCreationCount?: number;
  };
  contentProps?: {
    appPermissions?: HttpAppPermissions;
    apis?: {
      line?: {
        lineCacheTtlMin?: number;
      };
      busLocation?: {
        busLocationUpdateIntervalSecond?: number;
      };
      boarding?: {
        onBoardRequestIntervalSecond?: number;
      };
    };
    extensions?: {
      twc?: HttpTwcConfig;
    };
    pages?: {
      main?: {
        welcomeTexts: string[][];
        displayBlockDtmYn?: boolean;
        favoriteLinesViewType: string;
        favoriteLinesSortOrder?: string;
      };
      mainLogout?: {
        welcomeText: string[];
        csCenterPhone?: string;
      };
      joins?: HttpSignupInfo[];
      policy?: {
        agreeCodes: HttpTermsInfo[];
        terms: HttpTermsInfo[];
      };
      payLog?: {
        displayYn: boolean;
      };
      book?: {
        displayRemainBookCountYn?: boolean;
      };
      bookLog?: {
        displayYn: boolean;
      };
      boardLog?: {
        displayYn: boolean;
      };
      lineDetail?: {
        displayLineTurnListYn: boolean;
        displayLineTurnPathYn?: boolean;
        displayExtendedBusLocationMin?: number;
        sortOrder?: string;
      };
      stationLayouts?: HttpBusStopLayout[];
      verification?: {
        url: string;
      };
      lostAndFound?: {
        url: string;
        displayYn: boolean;
      };
      busCompanyList?: {
        url: string;
      };
      busCompany?: {
        url: string;
      };
    };
    style?: {
      logoPath?: string;
      color: HttpColor;
    };
    whiteListDomains?: string[];
  };
};

export type HttpServiceInfoResponse = {
  data: HttpServiceInfo;
};

export interface IServiceInfoParser {
  toDomain(data: HttpServiceInfo): Result<IServiceInfo, ParseError>;
}

export interface IServiceInfoRepository {
  getServiceInfo(): Promise<Result<IServiceInfo, ParseError | HttpError>>;
}
